import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { Badge, Button, styled } from "@mui/material";
import Icon from "@mui/material/Icon";
import {
  APPLY_LABEL,
  CANCEL_LABEL,
  RESET_LABEL,
} from "../../../theme/defaultConstants";
import { getJustificationByAlignment } from "../../builder/themeOptionsBuilder";
import SortButtonDropdown from "../ui/SortButtonDropdown";
import SearchBar from "../ui/SearchBar";
import {
  Alignment,
  InputSettings,
  OutlineColor,
  Position,
} from "../../types/theme";
import { useClearFilters } from "../ui/Filter";
import { PublicComponentProps } from "./PublicComponentProps";
import { PartnerResponse } from "../../dataAccess/api/partners";
import { CategoryResponse } from "../../dataAccess/api/categories";
import { CollectionHeader } from "../../dataAccess/api/collections";
import CategoriesFilter from "./CategoriesFilter";
import CollectionsFilter from "./CollectionsFilter";
import PartnersFilter from "./PartnersFilter";
import ButtonPopup from "../ui/ButtonPopup";
import { GridSettings, GridSubComponents } from "./Grid";
import OnlyMobile from "../ui/OnlyMobile";
import OnlyDesktop from "../ui/OnlyDesktop";
import { useQueryParams } from "../../dataAccess/QueryParams";
import { DropdownItem } from "../ui/DropdownList";
import { defaultFocusOutline } from "../../helpers/accessibility";
import { useTranslation } from "../../helpers/i18n";
import { useCapitalization } from "../../builder/Theme";
import { ThemeButton } from "../ui/ThemeComponents/ThemeButton";
import FiltersIcon from "../../assets/svg/filters.svg";
import { CloseButton } from "../ui/CloseButton";

export interface Settings {
  justification: Exclude<Alignment, "center">;
  alignment: Exclude<Alignment, "center">;
  position: Position;
  multipleSearch: boolean;
  mobileFilters: {
    variant: "primary" | "secondary";
    icon: string;
    applyLabel: string;
    resetLabel: string;
    cancelLabel: string;
  };
  sort: {
    hideSort: boolean;
    label: string;
    mobileSortIcon: string;
    recommendedLabel: string;
    popularityLabel: string;
    greatestSavingsLabel: string;
    aZLabel: string;
    ZaLabel: string;
    hideIcons: boolean;
  };
  sortOptions: Pick<DropdownItem, "value">[];
  placeholderText: string;
  input: OutlineColor & InputSettings;
}

interface Props
  extends PublicComponentProps<
    Settings,
    Record<string, never>,
    GridSubComponents,
    GridSettings
  > {
  partnerList?: PartnerResponse;
  categories?: CategoryResponse;
  collections?: CollectionHeader[];
}

interface ContainerProps {
  justification: Settings["justification"];
  position?: Settings["position"];
}

const SearchContainer = styled("div")<ContainerProps>(
  ({ theme, justification }) => ({
    width: "100%",
    display: "flex",
    gap: theme.spacing(1),
    flexDirection: justification === "left" ? "row" : "row-reverse",
    alignItems: "center",
  }),
);

const StyledButton = styled(Button)(({ theme }) => ({
  height: "2.5rem",
  maxWidth: "3.5rem",
  minWidth: "3.5rem",
  padding: "0 !important",
  border: "none !important",
  "&:hover": {
    backgroundColor: "transparent",
  },
  color: theme.palette.text.default,
  [theme.breakpoints.up("md")]: {
    color: theme.palette.inputs.paper,
    fontSize: theme.typography.body2.fontSize,
    width: "auto",
    textTransform: "none",
  },
}));

const FilterActionsButtonContainer = styled("div")(() => ({
  justifyContent: "flex-end",
  display: "flex",
  alignItems: "center",
}));

const StyledFilterIcon = styled(Icon)(({ theme }) => ({
  color: theme.palette.text.default,
}));

const StyledFilterIconImage = styled("img")(() => ({
  maxWidth: 24,
  maxHeight: 24,
}));

const SearchBarContainerDesktop = styled(OnlyDesktop)(() => ({
  flex: "1",
}));

const StyledSearchBar = styled(SearchBar)(() => ({
  flex: 1,
}));

const ButtonRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(2),
}));

const StyledThemeButton = styled(ThemeButton)(() => ({
  flex: 1,
}));

const FiltersIconContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  fontSize: theme.typography.button.fontSize,
  color: theme.palette.primary.main,
  "& span": {
    position: "relative",
    top: -2,
  },
}));

const FilterActionsButton = styled(Button)(({ theme }) => {
  const textTransform = useCapitalization(
    theme.settings.buttons?.capitalization,
  );
  return {
    fontWeight: "bold",
    textDecoration: "underline",
    textTransform,
    "&:hover": {
      textDecoration: "underline",
    },
    "&:focus": {
      outline: defaultFocusOutline(theme),
      outlineOffset: -3,
    },
  };
});

const SearchBarContainerMobile = styled(OnlyMobile)<
  Pick<Settings, "alignment">
>(({ theme, alignment }) => {
  const buttonSize = 30;
  return {
    display: "flex",
    justifyContent: getJustificationByAlignment(alignment),
    "& .MuiButton-root": {
      "&:focus": {
        outline: defaultFocusOutline(theme),
      },
      width: buttonSize,
      height: buttonSize,
      minWidth: buttonSize,
      background: "transparent",
      "& .MuiTouchRipple-root": {
        display: "none",
      },
    },
  };
});

const SearchBarContainerMobileContent = styled(OnlyMobile)<
  Pick<Settings, "justification">
>(({ theme, justification }) => ({
  display: "inline-flex",
  alignItems: "center",
  flex: 1,
  gap: theme.spacing(1),
  flexDirection: justification === "right" ? "row" : "row-reverse",
}));

const StyledCloseButton = styled(CloseButton)(() => ({
  position: "initial",
  alignSelf: "flex-end",
}));

const StyledCloseButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: theme.spacing(2),
}));

const MobileFiltersContainer = styled("div")(({ theme }) => ({
  maxHeight: "40vh",
  overflow: "auto",
  paddingRight: theme.spacing(1),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const Content = styled("div")(({ theme }) => ({
  padding: theme.spacing(2),
}));

function getSearchFilters(filters: (string | undefined)[]) {
  return filters
    .map((filterStr) => filterStr?.split(","))
    .flat()
    .filter((exist) => exist);
}

function useSearchFiltersFromUrl() {
  const { q, category_ids, collection_ids, partner_ids } = useQueryParams();
  return useMemo(
    () => ({
      filterList: getSearchFilters([category_ids, collection_ids, partner_ids]),
      searchList: getSearchFilters([q]),
    }),
    [q, category_ids, collection_ids, partner_ids],
  );
}

type FilterRef = { applyMobileFilters: () => void };

const Search: FunctionComponent<Props> = ({
  settings: {
    justification,
    multipleSearch,
    sortOptions,
    position,
    alignment,
    placeholderText,
    mobileFilters: mobileFiltersSettings,
    sort,
    input,
  },
  siblingsComponents,
  categories,
  collections,
  partnerList,
}: Props) => {
  const { t } = useTranslation();
  const { filterList } = useSearchFiltersFromUrl();
  const clearFilters = useClearFilters();
  const [openInMobile, setOpenInMobile] = useState(false);
  const categoriesFilterRef = useRef<FilterRef>(null);
  const collectionsFilterRef = useRef<FilterRef>(null);
  const partnersFilterRef = useRef<FilterRef>(null);
  const mobileVerticalFilters: Record<string, ReactNode> = useMemo(() => {
    const position = "vertical";
    return {
      CategoriesFilter: siblingsComponents?.CategoriesFilter && (
        <CategoriesFilter
          ref={categoriesFilterRef}
          subComponents={{}}
          mobile
          categories={categories}
          settings={{
            ...siblingsComponents?.CategoriesFilter.settings,
            position,
          }}
        />
      ),
      CollectionsFilter: siblingsComponents?.CollectionsFilter && (
        <CollectionsFilter
          ref={collectionsFilterRef}
          subComponents={{}}
          mobile
          collections={collections}
          settings={{
            ...siblingsComponents?.CollectionsFilter.settings,
            position,
          }}
        />
      ),
      PartnersFilter: siblingsComponents?.PartnersFilter && (
        <PartnersFilter
          ref={partnersFilterRef}
          subComponents={{}}
          mobile
          partnerList={partnerList}
          settings={{
            ...siblingsComponents?.PartnersFilter.settings,
            position,
          }}
        />
      ),
    };
  }, [categories, collections, partnerList, siblingsComponents]);

  const mobileFilters = useMemo(
    () =>
      Object.keys(siblingsComponents || {})
        .filter((key) => key !== "Search")
        .map((key) => mobileVerticalFilters[key]),
    [siblingsComponents, mobileVerticalFilters],
  );
  // TODO: Remove this after confirming the loading state is not needed for the search
  // if (loadingPLP) {
  //   return (
  //     <SearchContainerLoader justification={justification}>
  //       <SkeletonLoader variant="rectangular" height="3rem" width="90%" />
  //       <SkeletonLoader variant="rectangular" height="3rem" width="10%" />
  //     </SearchContainerLoader>
  //   );
  // }

  const filterIcon = useMemo(() => {
    if (mobileFiltersSettings?.icon) {
      if (mobileFiltersSettings.icon.startsWith("http")) {
        return (
          <StyledFilterIconImage
            src={mobileFiltersSettings.icon}
            alt={t("accessibility_filters")}
          />
        );
      }
      return <StyledFilterIcon>{mobileFiltersSettings.icon}</StyledFilterIcon>;
    }
    return <StyledFilterIcon>filter_alt</StyledFilterIcon>;
  }, [mobileFiltersSettings, t]);

  const handleClose = useCallback(() => {
    setOpenInMobile(false);
  }, [setOpenInMobile]);

  const handleApply = useCallback(() => {
    categoriesFilterRef.current?.applyMobileFilters();
    collectionsFilterRef.current?.applyMobileFilters();
    partnersFilterRef.current?.applyMobileFilters();
    handleClose();
  }, [handleClose]);

  const handleReset = useCallback(() => {
    clearFilters();
    handleClose();
  }, [handleClose, clearFilters]);

  return (
    <>
      <SearchContainer justification={justification} position={position}>
        <SearchBarContainerDesktop>
          <SearchBar
            multipleSearch={multipleSearch}
            alignment={alignment}
            input={input}
            placeholderText={placeholderText}
          />
        </SearchBarContainerDesktop>
        {!sort?.hideSort && (
          <OnlyDesktop>
            <div>
              <SortButtonDropdown options={sortOptions} settings={sort} />
            </div>
          </OnlyDesktop>
        )}
      </SearchContainer>
      <SearchBarContainerMobile alignment={alignment}>
        <SearchBarContainerMobileContent justification={justification}>
          {/* TODO: Replace this for DropdownList component */}
          {!sort?.hideSort && (
            <SortButtonDropdown
              options={sortOptions}
              settings={sort}
              aria-label={sort?.label}
            />
          )}
          {mobileFilters.length > 0 && (
            <ButtonPopup
              ButtonComponent={StyledButton}
              popupOpen={openInMobile}
              aria-expanded={openInMobile}
              setPopupOpen={setOpenInMobile}
              aria-label={t("accessibility_filters")}
              popupContent={
                <Content>
                  <div>
                    <StyledCloseButtonContainer>
                      <StyledCloseButton onClick={handleClose}>
                        <Icon>close</Icon>
                      </StyledCloseButton>
                    </StyledCloseButtonContainer>
                    <FilterActionsButtonContainer>
                      {mobileFiltersSettings?.variant === "secondary" ? (
                        <FiltersIconContainer>
                          <FiltersIcon /> <span>({filterList.length})</span>
                        </FiltersIconContainer>
                      ) : (
                        <FilterActionsButton
                          onClick={handleApply}
                          disableRipple
                        >
                          {mobileFiltersSettings?.applyLabel || APPLY_LABEL}
                        </FilterActionsButton>
                      )}
                      <FilterActionsButton
                        color={
                          mobileFiltersSettings?.variant === "secondary"
                            ? "primary"
                            : "error"
                        }
                        onClick={handleReset}
                        disableRipple
                      >
                        {mobileFiltersSettings?.resetLabel || RESET_LABEL}
                      </FilterActionsButton>
                    </FilterActionsButtonContainer>
                  </div>
                  <MobileFiltersContainer>
                    {mobileFilters}
                  </MobileFiltersContainer>

                  {mobileFiltersSettings?.variant === "secondary" && (
                    <ButtonRow>
                      <StyledThemeButton
                        customVariant="secondary"
                        onClick={handleClose}
                      >
                        {mobileFiltersSettings?.cancelLabel || CANCEL_LABEL}
                      </StyledThemeButton>
                      <StyledThemeButton onClick={handleApply}>
                        {mobileFiltersSettings?.applyLabel || APPLY_LABEL}
                      </StyledThemeButton>
                    </ButtonRow>
                  )}
                </Content>
              }
            >
              <Badge badgeContent={filterList.length} color="primary">
                {filterIcon}
              </Badge>
            </ButtonPopup>
          )}
          <StyledSearchBar
            multipleSearch={multipleSearch}
            alignment={alignment}
            input={input}
            placeholderText={placeholderText}
          />
        </SearchBarContainerMobileContent>
      </SearchBarContainerMobile>
    </>
  );
};

export default Search;
